import apiClient from '../client';

// Types
export interface BackupJob {
  id: number;
  job_id: string;
  job_name: string;
  backup_type: string;
  source_type: string;
  source_path?: string;
  destination_path?: string;
  schedule_enabled: boolean;
  schedule_cron?: string;
  next_run?: string;
  compression_enabled: boolean;
  encryption_enabled: boolean;
  retention_days: number;
  status: string;
  last_run_start?: string;
  last_run_end?: string;
  last_run_duration_seconds?: number;
  last_backup_size_bytes?: number;
  last_backup_file_count?: number;
  last_error_message?: string;
  total_runs: number;
  successful_runs: number;
  failed_runs: number;
  enabled: boolean;
  created_at: string;
}

export interface BackupHistory {
  id: number;
  execution_id: string;
  job_id: string;
  job_name: string;
  backup_type: string;
  status: string;
  started_at: string;
  completed_at?: string;
  duration_seconds?: number;
  backup_size_bytes?: number;
  file_count?: number;
  backup_path?: string;
  error_message?: string;
  error_details?: any;
}

export interface BackupHealth {
  success: boolean;
  health_check: {
    overall_health: string;
    health_score: number;
    issues_found: number;
    health_issues: string[];
    last_check: string;
    system_status: string;
    total_jobs: number;
    completed_jobs: number;
    failed_jobs: number;
    running_jobs: number;
    success_rate: number;
    total_backup_size_gb: number;
    backup_directory: string;
    recent_activity: Array<{
      job_id: string;
      job_name: string;
      status: string;
      last_run: string | null;
      duration_seconds: number;
      size_mb: number;
    }>;
    recommendations: string[];
  };
}

// API Functions
export const backupService = {
  // Get backup health check
  getHealthCheck: async (): Promise<BackupHealth> => {
    const response = await apiClient.get('/backup/health-check');
    return response.data;
  },

  // Get backup jobs
  getJobs: async (params?: {
    backup_type?: string;
    status?: string;
    enabled?: boolean;
    skip?: number;
    limit?: number;
  }): Promise<{ backup_jobs: BackupJob[]; total_count: number; success: boolean }> => {
    const response = await apiClient.get('/backup/jobs', { params });
    return response.data;
  },

  // Get job by ID
  getJob: async (jobId: string): Promise<{ success: boolean; job: BackupJob }> => {
    const response = await apiClient.get(`/backup/jobs/${jobId}`);
    return response.data;
  },

  // Create backup job
  createJob: async (data: Partial<BackupJob>): Promise<{ success: boolean; job: BackupJob }> => {
    const response = await apiClient.post('/backup/jobs', data);
    return response.data;
  },

  // Update backup job
  updateJob: async (jobId: string, data: Partial<BackupJob>): Promise<{ success: boolean; job: BackupJob }> => {
    const response = await apiClient.put(`/backup/jobs/${jobId}`, data);
    return response.data;
  },

  // Delete backup job
  deleteJob: async (jobId: string): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.delete(`/backup/jobs/${jobId}`);
    return response.data;
  },

  // Run backup job now
  runJob: async (jobId: string): Promise<{
    success: boolean;
    execution_id: string;
    message: string;
  }> => {
    const response = await apiClient.post(`/backup/jobs/${jobId}/run`);
    return response.data;
  },

  // Enable/disable job
  toggleJob: async (jobId: string, enabled: boolean): Promise<{ success: boolean; job: BackupJob }> => {
    const response = await apiClient.post(`/backup/jobs/${jobId}/toggle`, { enabled });
    return response.data;
  },

  // Get backup history (uses /jobs endpoint)
  getHistory: async (params?: {
    job_id?: string;
    status?: string;
    start_date?: string;
    end_date?: string;
    skip?: number;
    limit?: number;
  }): Promise<{ history: BackupHistory[]; total: number }> => {
    const response = await apiClient.get('/backup/jobs', { params });
    return response.data;
  },

  // Get execution details
  getExecution: async (executionId: string): Promise<{ success: boolean; execution: BackupHistory }> => {
    const response = await apiClient.get(`/backup/history/${executionId}`);
    return response.data;
  },

  // Get backup statistics
  getStatistics: async (days: number = 30): Promise<{
    success: boolean;
    statistics: {
      period_days: number;
      total_backups: number;
      successful_backups: number;
      failed_backups: number;
      success_rate: number;
      total_size_gb: number;
      average_duration_minutes: number;
      by_type: { [key: string]: number };
      by_status: { [key: string]: number };
      trend: Array<{ date: string; count: number; size_gb: number }>;
    };
  }> => {
    const response = await apiClient.get(`/backup/statistics?days=${days}`);
    return response.data;
  },

  // Restore from backup
  restoreBackup: async (executionId: string, data: {
    restore_path?: string;
    overwrite?: boolean;
    verify_before_restore?: boolean;
  }): Promise<{
    success: boolean;
    restore_id: string;
    message: string;
  }> => {
    const response = await apiClient.post(`/backup/restore/${executionId}`, data);
    return response.data;
  },

  // Verify backup
  verifyBackup: async (executionId: string): Promise<{
    success: boolean;
    verification: {
      valid: boolean;
      file_count: number;
      size_bytes: number;
      checksum_valid: boolean;
      issues: string[];
    };
  }> => {
    const response = await apiClient.post(`/backup/verify/${executionId}`);
    return response.data;
  },

  // Get storage usage
  getStorageUsage: async (): Promise<{
    success: boolean;
    storage: {
      total_backups: number;
      total_size_gb: number;
      by_type: { [key: string]: { count: number; size_gb: number } };
      oldest_backup: string;
      newest_backup: string;
      retention_compliance: {
        compliant: number;
        expired: number;
        expiring_soon: number;
      };
    };
  }> => {
    const response = await apiClient.get('/backup/storage-usage');
    return response.data;
  },

  // Cleanup old backups
  cleanupOldBackups: async (dryRun: boolean = true): Promise<{
    success: boolean;
    cleanup: {
      backups_to_delete: number;
      space_to_free_gb: number;
      deleted_count?: number;
      freed_space_gb?: number;
    };
  }> => {
    const response = await apiClient.post('/backup/cleanup', { dry_run: dryRun });
    return response.data;
  },
};

export default backupService;

